import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import { Notice, type NoticeAction } from '@/components/ui/primitives/Notice';

/** The caller's word for a toast; `error` draws the Notice's alert tone, the rest its info tone (D2: no hues). */
export type FeedbackVariant = 'success' | 'error' | 'warning' | 'info';

export interface FeedbackOptions {
  duration?: number;
  onRetry?: () => void;
  /** One labelled action (a receipt's `Show #4`); the toast closes once it ran. */
  action?: NoticeAction;
  /** Stop the dismiss clock while focus is inside the toast (set it whenever there is an action). */
  pauseOnFocus?: boolean;
  testId?: string;
}

interface FeedbackMessage extends FeedbackOptions {
  id: string;
  variant: FeedbackVariant;
  message: string;
}

interface FeedbackContextValue {
  messages: FeedbackMessage[];
  show: (message: string, variant?: FeedbackVariant, options?: FeedbackOptions) => string;
  remove: (id: string) => void;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

/** The feedback channel when one is mounted (null outside a FeedbackProvider: a surface that can say it itself). */
export function useOptionalFeedback(): FeedbackContextValue | null {
  return useContext(FeedbackContext);
}

export function useFeedback() {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within FeedbackProvider');
  }
  return context;
}

interface FeedbackProviderProps {
  children: ReactNode;
}

export function FeedbackProvider({ children }: FeedbackProviderProps) {
  const [messages, setMessages] = useState<FeedbackMessage[]>([]);

  const show = useCallback((
    message: string,
    variant: FeedbackVariant = 'info',
    options?: FeedbackOptions
  ) => {
    const id = `feedback-${Date.now()}-${Math.random()}`;
    const newMessage: FeedbackMessage = {
      ...options,
      id,
      message,
      variant,
      duration: options?.duration ?? 3000,
    };

    setMessages(prev => [...prev, newMessage]);
    return id;
  }, []);

  const remove = useCallback((id: string) => {
    setMessages(prev => prev.filter(msg => msg.id !== id));
  }, []);

  const value = useMemo(() => ({
    messages,
    show,
    remove
  }), [messages, show, remove]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackContainer messages={messages} onRemove={remove} />
    </FeedbackContext.Provider>
  );
}

interface FeedbackContainerProps {
  messages: FeedbackMessage[];
  onRemove: (id: string) => void;
}

function FeedbackContainer({ messages, onRemove }: FeedbackContainerProps) {
  if (messages.length === 0) return null;

  return (
    <div className="pmt-notice-stack">
      {messages.map((msg) => (
        <Notice
          key={msg.id}
          placement="toast"
          tone={msg.variant === 'error' ? 'alert' : 'info'}
          duration={msg.duration}
          onClose={() => onRemove(msg.id)}
          onRetry={msg.onRetry}
          action={msg.action}
          pauseOnFocus={msg.pauseOnFocus}
          testId={msg.testId}
          className="feedback-toast"
        >
          {msg.message}
        </Notice>
      ))}
    </div>
  );
}
